import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

/**
 * Wyciaga z bledu axios (po interceptorze w services/api.js) pola biznesowe
 * zwracane przez backend przy 409/422. Dziala tez gdy backend odpowie 200
 * z success:false - wtedy przekazujemy samo cialo odpowiedzi jako `zrodlo`.
 */
export function odczytajBladBackendu(zrodlo, domyslnyKomunikat = 'Blad operacji') {
  const d = zrodlo?.data || zrodlo || {};
  const sugerowany = d.sugerowany_krok != null ? Number(d.sugerowany_krok) : null;
  return {
    errorCode: d.error_code || zrodlo?.errorCode || null,
    wykryto: d.wykryto || null,
    oczekiwano: d.oczekiwano || null,
    sugerowanyKrok: (sugerowany === 1 || sugerowany === 2) ? sugerowany : null,
    diagnostyka: d.diagnostyka || null,
    brakujace: Array.isArray(d.brakujace) ? d.brakujace : null,
    // FAKTURA_ISTNIEJE (wspolna bramka duplikatow): pelny opis istniejacego dokumentu
    // {id, numer, sklep, sklep_id, dodano, kto}. Aliasy 'istniejaca_faktura_id' /
    // 'invoice_id' zostaja dla starszych odpowiedzi (DUPLIKAT_FAKTURY).
    istniejaca: d.istniejaca || null,
    istniejacaFakturaId: d.istniejaca?.id || d.istniejaca_faktura_id || d.invoice_id || null,
    // Czy TEN uzytkownik moze wymusic przetworzenie mimo rozpoznania typu pliku
    moznaWymusic: d.mozna_wymusic === true,
    komunikat: d.message || d.error || zrodlo?.message || domyslnyKomunikat
  };
}

/**
 * Custom hook encapsulating all purchase invoice import logic:
 * - Menu stats & warehouse loading
 * - File upload (XML, PDF, paragon, cennik, Optima, SFD)
 * - Optima import preview/save
 * - Cennik import preview/save (single & multi)
 * - Faktura XML preview/save
 */
export default function usePurchaseImports(currentLocationId, opcje = {}) {
  const navigate = useNavigate();
  // autoNavigate=false uzywa Kreator faktury - bez tego po 1.5s wyrzucaloby
  // uzytkownika z kroku 2 na strone szczegolow faktury.
  const { autoNavigate = true } = opcje;

  // Timeout dla operacji na cennikach - save_cennik_products robi 1-2 zapytania
  // na produkt, wiec duzy plik nie moze cicho zawiesic kasy.
  const CENNIK_TIMEOUT = 120000;

  // Jeden ref na interwal postepu - czyszczony przy odmontowaniu komponentu.
  const progressIntervalRef = useRef(null);
  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);
  useEffect(() => () => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
  }, []);

  // --- core data ---
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  // --- upload progress ---
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // --- Cennik only ---
  const [cennikOnlyFile, setCennikOnlyFile] = useState(null);
  const [showCennikOnlyModal, setShowCennikOnlyModal] = useState(false);
  const [cennikOnlyPreview, setCennikOnlyPreview] = useState(null);

  const handleCennikOnlyImport = useCallback(async () => {
    if (!cennikOnlyFile) return;
    try {
      const formData = new FormData();
      formData.append('cennik_file', cennikOnlyFile);
      const res = await api.post('/purchase-invoices/import-cennik-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        setCennikOnlyPreview(res.data.data);
        setShowCennikOnlyModal(true);
      } else {
        toast.error(res.data?.error || 'Błąd');
      }
    } catch (e) {
      toast.error(e?.message || 'Błąd importu cennika');
    }
  }, [cennikOnlyFile]);

  const handleCennikOnlySave = useCallback(async () => {
    if (!cennikOnlyFile) return;
    try {
      const formData = new FormData();
      formData.append('cennik_file', cennikOnlyFile);
      formData.append('apply', 'true');
      const res = await api.post('/purchase-invoices/import-cennik-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        setShowCennikOnlyModal(false);
        setCennikOnlyFile(null);
        setCennikOnlyPreview(null);
      } else {
        toast.error(res.data?.error || 'Błąd');
      }
    } catch (e) {
      toast.error(e?.message || 'Błąd zapisu cennika');
    }
  }, [cennikOnlyFile]);

  // --- Optima ---
  const [showOptimaModal, setShowOptimaModal] = useState(false);
  const [optimaPreview, setOptimaPreview] = useState(null);
  const [optimaOptions, setOptimaOptions] = useState({
    update_prices: true,
    update_stock: true,
    create_missing: true,
    warehouse_id: null
  });
  const [optimaFile, setOptimaFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // --- Cennik (single) ---
  const [showCennikModal, setShowCennikModal] = useState(false);
  const [cennikPreview, setCennikPreview] = useState(null);
  const [cennikOptions, setCennikOptions] = useState({
    update_names: true,
    update_prices: true,
    create_new: true
  });
  const [cennikFilter, setCennikFilter] = useState('all');
  const [cennikSearch, setCennikSearch] = useState('');
  const [isSavingCennik, setIsSavingCennik] = useState(false);

  // --- Cennik (multi) ---
  const [multiCennikProgress, setMultiCennikProgress] = useState(null);
  const [multiCennikPreview, setMultiCennikPreview] = useState(null);
  const [showMultiCennikModal, setShowMultiCennikModal] = useState(false);
  const [multiCennikFilter, setMultiCennikFilter] = useState('all');
  const [multiCennikSearch, setMultiCennikSearch] = useState('');
  const [isSavingMultiCennik, setIsSavingMultiCennik] = useState(false);

  // --- Paragony XML (multi) ---
  const [multiParagonProgress, setMultiParagonProgress] = useState(null);
  const [multiParagonWynik, setMultiParagonWynik] = useState(null);
  const [showMultiParagonModal, setShowMultiParagonModal] = useState(false);

  // --- Faktura XML ---
  const [showFakturaModal, setShowFakturaModal] = useState(false);
  const [fakturaPreview, setFakturaPreview] = useState(null);
  const [isSavingFaktura, setIsSavingFaktura] = useState(false);

  // --- PDF option ---
  const [updateProductNames, setUpdateProductNames] = useState(true);

  // --- PDF + XML validation flow ---
  const [pendingPdfFile, setPendingPdfFile] = useState(null);
  const [pendingPdfType, setPendingPdfType] = useState(null);

  // --- PDF Products preview ---
  const [showPdfProductsModal, setShowPdfProductsModal] = useState(false);
  const [pdfProductsPreview, setPdfProductsPreview] = useState(null);
  const [pdfProductsOptions, setPdfProductsOptions] = useState({ update_names: true, create_new: true });
  const [pdfProductsFilter, setPdfProductsFilter] = useState('all');
  const [pdfProductsSearch, setPdfProductsSearch] = useState('');
  const [isSavingPdfProducts, setIsSavingPdfProducts] = useState(false);

  // --- file input refs ---
  const xmlInputRef = useRef(null);
  const paragonInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const pdfProductsInputRef = useRef(null);
  const cennikInputRef = useRef(null);
  const cennikMultipleInputRef = useRef(null);
  const paragonMultipleInputRef = useRef(null);
  const optimaInputRef = useRef(null);
  const sfdInputRef = useRef(null);
  const xmlValidationInputRef = useRef(null);
  const optimaUpdateCennikRef = useRef(null);
  const optimaUpdateDokumentRef = useRef(null);

  // Optima update (cennik + dokument)
  const [optimaUpdateCennikFile, setOptimaUpdateCennikFile] = useState(null);
  const [optimaUpdateDokumentFile, setOptimaUpdateDokumentFile] = useState(null);
  const [optimaUpdatePreview, setOptimaUpdatePreview] = useState(null);
  const [showOptimaUpdateModal, setShowOptimaUpdateModal] = useState(false);

  const handleOptimaUpdatePreview = useCallback(async () => {
    if (!optimaUpdateCennikFile || !optimaUpdateDokumentFile) return;
    const formData = new FormData();
    formData.append('cennik_file', optimaUpdateCennikFile);
    formData.append('dokument_file', optimaUpdateDokumentFile);
    formData.append('preview', 'true');
    if (currentLocationId) formData.append('location_id', currentLocationId);

    try {
      setLoading(true);
      const res = await api.post('/purchase-invoices/import-optima-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      if (res.data?.success) {
        setOptimaUpdatePreview(res.data.data);
        setShowOptimaUpdateModal(true);
      } else {
        toast.error(res.data?.error || 'Błąd parsowania');
      }
    } catch (e) {
      toast.error(e?.message || 'Błąd importu');
    } finally {
      setLoading(false);
    }
  }, [optimaUpdateCennikFile, optimaUpdateDokumentFile, currentLocationId]);

  const handleOptimaUpdateSave = useCallback(async (options = {}) => {
    if (!optimaUpdateCennikFile || !optimaUpdateDokumentFile) return;
    const formData = new FormData();
    formData.append('cennik_file', optimaUpdateCennikFile);
    formData.append('dokument_file', optimaUpdateDokumentFile);
    formData.append('preview', 'false');
    formData.append('update_names', options.update_names !== false ? 'true' : 'false');
    formData.append('update_purchase', options.update_purchase !== false ? 'true' : 'false');
    formData.append('update_retail', options.update_retail !== false ? 'true' : 'false');
    formData.append('create_new', options.create_new !== false ? 'true' : 'false');
    if (currentLocationId) formData.append('location_id', currentLocationId);

    try {
      setLoading(true);
      const res = await api.post('/purchase-invoices/import-optima-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      if (res.data?.success) {
        toast.success(res.data.message || 'Ceny zaktualizowane');
        setOptimaUpdateCennikFile(null);
        setOptimaUpdateDokumentFile(null);
        setShowOptimaUpdateModal(false);
        setOptimaUpdatePreview(null);
      } else {
        toast.error(res.data?.error || 'Błąd aktualizacji');
      }
    } catch (e) {
      toast.error(e?.message || 'Błąd importu');
    } finally {
      setLoading(false);
    }
  }, [optimaUpdateCennikFile, optimaUpdateDokumentFile, currentLocationId]);

  // ===================== DATA LOADING =====================

  const loadMenuStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = currentLocationId ? { location_id: currentLocationId } : {};
      const response = await api.get('/purchase-invoices/menu', { params });
      const data = response.data;
      if (data.success) setStats(data.data);
      else setError(data.message);
    } catch (err) {
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  }, [currentLocationId]);

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await api.get('/warehouses');
      const data = response.data;
      if (data.success) {
        setWarehouses(data.data || []);
        if (data.data?.length === 1) {
          setOptimaOptions(prev => ({ ...prev, warehouse_id: data.data[0].location_id }));
        }
      }
    } catch (err) {
      console.error('Blad ladowania magazynow:', err);
    }
  }, []);

  useEffect(() => {
    loadMenuStats();
    loadWarehouses();
  }, [loadMenuStats, loadWarehouses]);

  // ===================== OPTIMA IMPORT =====================

  const handleOptimaUpload = async (file) => {
    if (!file) return;
    setOptimaFile(file);
    setUploadProgress(0);
    setUploadStatus({ type: 'optima', status: 'uploading', message: 'Analizowanie pliku Optima...' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('preview', 'true');

    try {
      setUploadProgress(30);
      const response = await api.post('/purchase-invoices/import-optima', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadProgress(70);
      const data = response.data;

      if (data.success) {
        setOptimaPreview({ ...data.data, file });
        setShowOptimaModal(true);
        const s = data.data.stats || {};
        setUploadStatus({
          type: 'optima',
          status: 'success',
          message: `Znaleziono ${s.found || 0} produktow, ${s.will_create || 0} do utworzenia`
        });
      } else {
        setUploadStatus({ type: 'optima', status: 'error', message: data.message });
      }
      setUploadProgress(100);
    } catch (err) {
      setUploadStatus({ type: 'optima', status: 'error', message: 'Blad przesylania pliku' });
      setUploadProgress(0);
    }
  };

  const saveOptimaImport = async () => {
    if (!optimaPreview?.products || !optimaFile) {
      setUploadStatus({ type: 'optima', status: 'error', message: 'Brak pliku do importu' });
      return;
    }
    if (isSaving) return;
    setIsSaving(true);

    setUploadStatus({ type: 'optima', status: 'uploading', message: 'Tworzenie faktury zakupowej...' });

    try {
      const formData = new FormData();
      formData.append('file', optimaFile);
      formData.append('preview', 'false');
      formData.append('create_missing', optimaOptions.create_missing !== false ? 'true' : 'false');
      if (optimaOptions.warehouse_id) {
        formData.append('warehouse_id', optimaOptions.warehouse_id);
      }

      const response = await api.post('/purchase-invoices/import-optima', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = response.data;

      if (data.success) {
        const s = data.data.stats || {};
        const invoiceId = data.data.invoice_id;
        let message = `✅ Utworzono fakturę #${invoiceId}: ${s.added_items || 0} pozycji`;
        if (s.created_products > 0) {
          message += `, ${s.created_products} nowych produktów`;
        }
        message += `. Teraz wygeneruj PZ w liście faktur!`;

        setUploadStatus({ type: 'optima', status: 'success', message });
        setShowOptimaModal(false);
        setOptimaPreview(null);
        setOptimaFile(null);
        loadMenuStats();

        toast.success(`Faktura utworzona: ${data.data.invoice_number}, pozycji: ${s.added_items || 0}, nowych produktów: ${s.created_products || 0}`);
      } else {
        setUploadStatus({ type: 'optima', status: 'error', message: data.message });
      }
    } catch (err) {
      setUploadStatus({ type: 'optima', status: 'error', message: 'Blad zapisywania' });
    } finally {
      setIsSaving(false);
    }
  };

  // ===================== SFD IMPORT =====================

  const handleSfdUpload = async (file) => {
    if (!file) return;
    setUploadProgress(0);
    setUploadStatus({ type: 'sfd', status: 'uploading', message: 'Parsowanie pliku SFD...' });

    const formData = new FormData();
    formData.append('xml_file', file);
    if (currentLocationId) formData.append('location_id', currentLocationId);

    try {
      setUploadProgress(30);
      const response = await api.post('/purchase-invoices/import-sfd', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadProgress(70);
      const data = response.data;

      if (data.success) {
        setUploadProgress(100);
        setUploadStatus({
          type: 'sfd',
          status: 'success',
          message: `Faktura ${data.data?.invoice?.numer_faktury || ''} zaimportowana (${data.data?.stats?.total_items || 0} pozycji)`
        });
        loadMenuStats();
      } else {
        setUploadStatus({ type: 'sfd', status: 'error', message: data.message || 'Blad importu' });
      }
    } catch (err) {
      setUploadStatus({ type: 'sfd', status: 'error', message: 'Blad przesylania pliku' });
      setUploadProgress(0);
    }
  };

  // ===================== CENNIK IMPORT (single) =====================

  const saveCennikImport = async () => {
    if (!cennikPreview?.products) {
      setUploadStatus({ type: 'cennik', status: 'error', message: 'Brak produktow do importu' });
      return { ok: false, stats: null };
    }
    if (isSavingCennik) return { ok: false, stats: null };
    setIsSavingCennik(true);

    setUploadStatus({ type: 'cennik', status: 'uploading', message: 'Zapisywanie cennika...' });

    // Nazwa pliku potrzebna Kreatorowi do podsumowania - preview jest kasowany po zapisie.
    const nazwaPliku = cennikPreview.filename;
    const liczbaPozycji = cennikPreview.stats?.total || cennikPreview.products.length || 0;

    try {
      const response = await api.post('/purchase-invoices/save-cennik', {
        filename: cennikPreview.filename,
        products: cennikPreview.products,
        update_names: cennikOptions.update_names,
        update_prices: cennikOptions.update_prices,
        create_new: cennikOptions.create_new
      }, { timeout: CENNIK_TIMEOUT });

      const data = response.data;

      if (data.success) {
        const s = data.data || {};
        let message = `✅ Cennik zaimportowany: ${s.created || 0} nowych, ${s.updated || 0} zaktualizowanych`;
        if (s.names_updated > 0) message += `, ${s.names_updated} zmian nazw`;
        if (s.skipped > 0) message += `, ${s.skipped} pominiętych`;
        // Odrzucone MUSZA byc widoczne: cennik bez stawek VAT nie tworzy ani
        // jednego produktu, a komunikat "0 nowych" nie tlumaczyl dlaczego.
        if (s.odrzucone > 0) {
          message += `, ⛔ ${s.odrzucone} odrzuconych (brak nazwy lub stawki VAT w pliku)`;
        }
        if (s.nazwy_odrzucone > 0) {
          message += `, ${s.nazwy_odrzucone} nazw nie zmieniono (pusta nazwa w pliku)`;
        }

        setUploadStatus({
          type: 'cennik',
          status: (s.odrzucone > 0 && !s.created && !s.updated) ? 'error' : 'success',
          message
        });
        if (s.odrzucone > 0) toast.error(`Cennik: odrzucono ${s.odrzucone} pozycji (brak nazwy lub stawki VAT)`);
        setShowCennikModal(false);
        setCennikPreview(null);
        loadMenuStats();
        return { ok: true, stats: { ...s, filename: nazwaPliku, total: liczbaPozycji } };
      }
      setUploadStatus({ type: 'cennik', status: 'error', message: data.message });
      return { ok: false, stats: null };
    } catch (err) {
      const message = err?.isTimeout
        ? 'Cennik jest duzy, przetwarzanie moze potrwac do 2 minut. Odswiez liste produktow przed ponowna proba.'
        : (odczytajBladBackendu(err, 'Blad zapisywania cennika').komunikat || 'Blad zapisywania cennika');
      setUploadStatus({ type: 'cennik', status: 'error', message });
      return { ok: false, stats: null };
    } finally {
      setIsSavingCennik(false);
    }
  };

  const getFilteredCennikProducts = useCallback(() => {
    if (!cennikPreview?.products) return [];

    let filtered = cennikPreview.products;

    if (cennikFilter === 'mapped') filtered = filtered.filter(p => p.is_mapped);
    else if (cennikFilter === 'new') filtered = filtered.filter(p => !p.is_mapped);
    else if (cennikFilter === 'name_changes') filtered = filtered.filter(p => p.is_mapped && p.name_differs);

    if (cennikSearch.trim()) {
      const search = cennikSearch.toLowerCase();
      filtered = filtered.filter(p =>
        (p.nazwa || '').toLowerCase().includes(search) ||
        (p.kod || '').toLowerCase().includes(search) ||
        (p.ean || '').toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [cennikPreview, cennikFilter, cennikSearch]);

  // ===================== MULTI CENNIK IMPORT =====================

  const handleMultipleCennikUpload = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setMultiCennikProgress({ total: fileArray.length, current: 0, processing: true });

    const allProducts = [];
    const filesSummary = [];
    let totalMapped = 0, totalNew = 0, totalNameChanges = 0;

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      setMultiCennikProgress(prev => ({ ...prev, current: i + 1, currentFile: file.name }));

      try {
        const formData = new FormData();
        formData.append('cennik_file', file);

        const parseResponse = await api.post('/purchase-invoices/import-cennik', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: CENNIK_TIMEOUT
        });
        const parseData = parseResponse.data;

        if (parseData.success && parseData.data?.products) {
          const products = parseData.data.products.map(p => ({ ...p, source_file: file.name }));
          allProducts.push(...products);

          const s = parseData.data.stats || {};
          totalMapped += s.mapped || 0;
          totalNew += s.new || 0;
          totalNameChanges += s.name_changes || 0;

          filesSummary.push({ file: file.name, success: true, count: products.length, mapped: s.mapped || 0, new: s.new || 0 });
        } else {
          filesSummary.push({ file: file.name, success: false, message: parseData.message || 'Blad parsowania' });
        }
      } catch (err) {
        filesSummary.push({ file: file.name, success: false, message: 'Blad polaczenia' });
      }
    }

    setMultiCennikProgress(null);

    if (allProducts.length > 0) {
      setMultiCennikPreview({
        products: allProducts,
        files: filesSummary,
        stats: {
          total: allProducts.length,
          mapped: totalMapped,
          new: totalNew,
          name_changes: totalNameChanges,
          files_count: filesSummary.filter(f => f.success).length
        }
      });
      setShowMultiCennikModal(true);
    } else {
      setUploadStatus({
        type: 'cennik',
        status: 'error',
        message: 'Nie udalo sie wczytac zadnych produktow z wybranych plikow'
      });
    }
  };

  const saveMultiCennikImport = async () => {
    if (!multiCennikPreview?.products) return { ok: false, stats: null };
    if (isSavingMultiCennik) return { ok: false, stats: null };

    setIsSavingMultiCennik(true);
    const liczbaPlikow = multiCennikPreview.stats?.files_count || 0;
    const liczbaPozycji = multiCennikPreview.stats?.total || multiCennikPreview.products.length || 0;

    try {
      const response = await api.post('/purchase-invoices/save-cennik', {
        filename: `multi_import_${multiCennikPreview.stats.files_count}_files`,
        products: multiCennikPreview.products,
        update_names: cennikOptions.update_names,
        update_prices: cennikOptions.update_prices,
        create_new: cennikOptions.create_new
      }, { timeout: CENNIK_TIMEOUT });

      const data = response.data;

      if (data.success) {
        const s = data.data || {};
        setUploadStatus({
          type: 'cennik',
          status: 'success',
          message: `✅ Zaimportowano ${multiCennikPreview.stats.files_count} cenników: ${s.created || 0} nowych, ${s.updated || 0} zaktualizowanych`
        });
        setShowMultiCennikModal(false);
        setMultiCennikPreview(null);
        loadMenuStats();
        return {
          ok: true,
          stats: { ...s, filename: `${liczbaPlikow} plików cennika`, total: liczbaPozycji }
        };
      }
      setUploadStatus({ type: 'cennik', status: 'error', message: data.message });
      return { ok: false, stats: null };
    } catch (err) {
      const message = err?.isTimeout
        ? 'Cennik jest duzy, przetwarzanie moze potrwac do 2 minut. Odswiez liste produktow przed ponowna proba.'
        : 'Blad zapisywania';
      setUploadStatus({ type: 'cennik', status: 'error', message });
      return { ok: false, stats: null };
    } finally {
      setIsSavingMultiCennik(false);
    }
  };

  // ===================== MULTI PARAGON IMPORT =====================
  // Zbiorczy import paragonow z NIP: uzytkownik zaznacza kilka plikow XML,
  // serwer zapisuje je OD RAZU (bez podgladu per plik), duplikaty pomija,
  // a na koncu pokazujemy raport per plik. Paczka idzie w porcjach, zeby
  // jedno zadanie HTTP nie trwalo minutami przy kilkudziesieciu plikach.
  const PARAGONY_NA_ZADANIE = 10;

  const handleMultipleParagonUpload = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setMultiParagonProgress({
      total: fileArray.length, current: 0, processing: true, currentFile: fileArray[0]?.name
    });

    const wyniki = [];
    for (let start = 0; start < fileArray.length; start += PARAGONY_NA_ZADANIE) {
      const porcja = fileArray.slice(start, start + PARAGONY_NA_ZADANIE);
      setMultiParagonProgress(prev => ({ ...prev, current: start, currentFile: porcja[0].name }));

      const formData = new FormData();
      porcja.forEach(f => formData.append('xml_files', f));
      if (currentLocationId) formData.append('location_id', currentLocationId);

      try {
        const response = await api.post('/purchase-invoices/import-paragony-xml', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: CENNIK_TIMEOUT
        });
        const data = response.data;
        if (data.success && Array.isArray(data.data?.wyniki)) {
          wyniki.push(...data.data.wyniki);
        } else {
          const b = odczytajBladBackendu(data, 'Blad importu paragonow');
          porcja.forEach(f => wyniki.push({ plik: f.name, status: 'blad', komunikat: b.komunikat }));
        }
      } catch (err) {
        // Cala porcja nie doszla (siec, 429, 500) - kazdy jej plik dostaje ten
        // sam komunikat, zeby raport mial komplet wierszy i nic nie zniknelo.
        const b = odczytajBladBackendu(err, 'Blad polaczenia');
        porcja.forEach(f => wyniki.push({ plik: f.name, status: 'blad', komunikat: b.komunikat }));
      }

      setMultiParagonProgress(prev => ({
        ...prev, current: Math.min(start + porcja.length, fileArray.length)
      }));
    }

    setMultiParagonProgress(null);

    const zapisaneLista = wyniki.filter(w => w.status === 'zapisany');
    const zapisane = zapisaneLista.length;
    const duplikaty = wyniki.filter(w => w.status === 'duplikat').length;
    const bledy = wyniki.filter(w => w.status === 'blad').length;
    const sumaBrutto = zapisaneLista.reduce((s, w) => s + (parseFloat(w.suma_brutto) || 0), 0);

    setMultiParagonWynik({
      wyniki,
      podsumowanie: {
        plikow: wyniki.length, zapisane, duplikaty, bledy,
        suma_brutto_zapisanych: Math.round(sumaBrutto * 100) / 100
      }
    });
    setShowMultiParagonModal(true);

    const komunikat = `Paragony: zapisano ${zapisane} z ${wyniki.length}`
      + (duplikaty ? `, duplikaty: ${duplikaty}` : '')
      + (bledy ? `, bledy: ${bledy}` : '');
    setUploadStatus({ type: 'paragon', status: bledy === 0 ? 'success' : 'error', message: komunikat });
    if (zapisane > 0) loadMenuStats();
  };

  const closeMultiParagonModal = () => {
    setShowMultiParagonModal(false);
    setMultiParagonWynik(null);
  };

  const getFilteredMultiCennikProducts = useCallback(() => {
    if (!multiCennikPreview?.products) return [];

    let filtered = [...multiCennikPreview.products];

    if (multiCennikFilter === 'mapped') filtered = filtered.filter(p => p.is_mapped);
    else if (multiCennikFilter === 'new') filtered = filtered.filter(p => !p.is_mapped);
    else if (multiCennikFilter === 'name_changes') filtered = filtered.filter(p => p.is_mapped && p.name_differs);

    if (multiCennikSearch) {
      const search = multiCennikSearch.toLowerCase();
      filtered = filtered.filter(p =>
        (p.nazwa || '').toLowerCase().includes(search) ||
        (p.kod || '').toLowerCase().includes(search) ||
        (p.ean || '').toLowerCase().includes(search) ||
        (p.source_file || '').toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [multiCennikPreview, multiCennikFilter, multiCennikSearch]);

  // ===================== FAKTURA XML IMPORT =====================

  const saveFakturaImport = async (opcje = {}) => {
    const pusty = { ok: false, invoiceId: null, errorCode: null, brakujace: null, komunikat: '' };

    if (!fakturaPreview?.invoice) {
      const msg = 'Brak danych faktury';
      setUploadStatus({ type: 'xml', status: 'error', message: msg });
      toast.error(msg);
      return { ...pusty, komunikat: msg };
    }

    // Faktura bez ani jednej pozycji nie jest faktura - backend ja odrzuci,
    // ale lepiej powiedziec to od razu i konkretnie.
    if (!Array.isArray(fakturaPreview.items) || fakturaPreview.items.length === 0) {
      const msg = 'Ten plik nie zawiera zadnej pozycji faktury — sprawdz, czy to na pewno faktura';
      setUploadStatus({ type: 'xml', status: 'error', message: msg });
      toast.error(msg);
      return { ...pusty, komunikat: msg };
    }

    const numerFaktury = fakturaPreview.invoice?.numer_faktury;

    // Duplikat numeru: serwer odrzuca ZAWSZE (409 FAKTURA_ISTNIEJE, wspolna bramka
    // dla calego systemu - numer unikalny niezaleznie od sklepu i dostawcy).
    // Nie ma juz potwierdzenia "zapisz mimo to" - decyzja wlasciciela po 11
    // podwojnych fakturach na produkcji (2026-09-13). Gdy podglad juz wie, ze
    // numer jest zajety, nie wysylamy zadania - odpowiadamy tym samym co serwer.
    if (fakturaPreview.already_exists) {
      const ist = fakturaPreview.istniejaca || null;
      const komunikat = ist
        ? `Faktura ${numerFaktury} jest już w systemie (${ist.sklep || 'bez sklepu'}, id ${ist.id}${ist.dodano ? `, dodana ${ist.dodano}` : ''}${ist.kto ? ` przez ${ist.kto}` : ''}). Nie dodano drugi raz.`
        : `Faktura ${numerFaktury} jest już w systemie. Nie dodano drugi raz.`;
      setUploadStatus({ type: 'xml', status: 'error', message: komunikat });
      toast.error(komunikat);
      return {
        ...pusty, errorCode: 'FAKTURA_ISTNIEJE', komunikat,
        istniejaca: ist, istniejacaFakturaId: ist?.id || null, invoiceId: ist?.id || null
      };
    }

    if (isSavingFaktura) return pusty;
    setIsSavingFaktura(true);

    setUploadStatus({ type: 'xml', status: 'uploading', message: 'Zapisywanie faktury...' });

    try {
      const response = await api.post('/purchase-invoices/save-xml', {
        invoice: fakturaPreview.invoice,
        items: fakturaPreview.items,
        filename: fakturaPreview.filename,
        location_id: currentLocationId
      });

      const data = response.data;

      if (data.success) {
        const komunikat = `Faktura ${numerFaktury} zostala zaimportowana`;
        setUploadStatus({ type: 'xml', status: 'success', message: komunikat });
        setShowFakturaModal(false);
        setFakturaPreview(null);
        loadMenuStats();
        if (data.data?.invoice_id && autoNavigate) {
          setTimeout(() => navigate(`/purchase-invoices/${data.data.invoice_id}`), 1500);
        }
        return { ok: true, invoiceId: data.data?.invoice_id || null, errorCode: null, brakujace: null, komunikat };
      }
      // Backend moze zwrocic 200 z success:false i kodem biznesowym
      const b = odczytajBladBackendu(data, data.message || 'Blad zapisywania faktury');
      setUploadStatus({ type: 'xml', status: 'error', message: b.komunikat });
      // Modal podgladu to overlay position:fixed - pasek statusu jest POD nim,
      // wiec bez toasta nieudany zapis wygladal jak "nic sie nie stalo".
      toast.error(b.komunikat);
      return {
        ok: false, invoiceId: null, errorCode: b.errorCode,
        brakujace: b.brakujace, istniejacaFakturaId: b.istniejacaFakturaId, istniejaca: b.istniejaca,
        komunikat: b.komunikat
      };
    } catch (err) {
      const b = odczytajBladBackendu(err, 'Blad zapisywania faktury');
      let komunikat = b.komunikat;
      if (b.errorCode === 'CENNIK_WYMAGANY') {
        komunikat = b.komunikat || 'Faktura zawiera pozycje, ktorych nie ma w systemie - najpierw wgraj cennik';
      } else if (b.errorCode === 'FAKTURA_ISTNIEJE' || b.errorCode === 'DUPLIKAT_FAKTURY') {
        komunikat = b.komunikat || `Faktura ${numerFaktury} jest już w systemie. Nie dodano drugi raz.`;
      }
      setUploadStatus({ type: 'xml', status: 'error', message: komunikat });
      toast.error(komunikat);
      return {
        ok: false,
        // FAKTURA_ISTNIEJE: 'istniejaca' {id, numer, sklep, ...}; starsze: 'istniejaca_faktura_id' / 'invoice_id'
        invoiceId: b.istniejacaFakturaId,
        istniejacaFakturaId: b.istniejacaFakturaId,
        istniejaca: b.istniejaca,
        errorCode: b.errorCode,
        brakujace: b.brakujace,
        komunikat
      };
    } finally {
      setIsSavingFaktura(false);
    }
  };

  // ===================== PDF + XML VALIDATION FLOW =====================

  const onPdfSelect = useCallback((type, file) => {
    if (!file) return;
    setPendingPdfFile(file);
    setPendingPdfType(type);
    // Po wybraniu PDF, w UI pojawi sie input na XML
    // XML input otworzy sie automatycznie po renderze
    toast('Teraz wybierz plik XML walidacyjny od dostawcy', { icon: '📄' });
  }, []);

  const onXmlValidationSelect = useCallback((xmlFile) => {
    if (!xmlFile || !pendingPdfFile || !pendingPdfType) {
      toast.error('Najpierw wybierz plik PDF');
      return;
    }
    // Mamy oba pliki — startuj upload
    handlePdfWithXmlUpload(pendingPdfType, pendingPdfFile, xmlFile);
    setPendingPdfFile(null);
    setPendingPdfType(null);
  }, [pendingPdfFile, pendingPdfType]);

  const handlePdfWithXmlUpload = async (type, pdfFile, xmlFile) => {
    setUploadProgress(0);
    setUploadStatus({ type, status: 'uploading', message: 'Przesylanie PDF + XML...' });

    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    formData.append('xml_validation_file', xmlFile);

    if (currentLocationId) formData.append('location_id', currentLocationId);
    if (type === 'pdf') formData.append('update_product_names', updateProductNames.toString());

    const progressSteps = [
      { percent: 10, msg: 'Przesylanie plikow...' },
      { percent: 20, msg: 'Parsowanie XML walidacyjnego...' },
      { percent: 35, msg: 'Konwersja PDF na obrazy...' },
      { percent: 55, msg: 'Skanowanie kodow kreskowych...' },
      { percent: 70, msg: 'Walidacja pozycji PDF vs XML...' },
      { percent: 85, msg: 'Mapowanie produktow...' },
      { percent: 95, msg: 'Zapisywanie...' }
    ];

    let stepIndex = 0;
    clearProgressInterval();
    progressIntervalRef.current = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setUploadProgress(progressSteps[stepIndex].percent);
        setUploadStatus({ type, status: 'uploading', message: progressSteps[stepIndex].msg });
        stepIndex++;
      }
    }, 3000);

    try {
      if (type === 'pdf-products') {
        // Preview produktow z walidacja XML
        const response = await api.post('/purchase-invoices/preview-pdf-products', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = response.data;
        clearProgressInterval();
        setUploadProgress(100);

        if (!data.success) {
          setUploadStatus({ type, status: 'error', message: data.message });
          return;
        }

        setPdfProductsPreview(data.data);
        setShowPdfProductsModal(true);
        const s = data.data.stats || {};
        let msg = `Znaleziono ${s.total || 0} produktow (${s.mapped || 0} zmapowanych, ${s.new || 0} nowych)`;
        if (s.xml_mismatched > 0) {
          msg += ` | XML: ${s.xml_mismatched} niezgodnych!`;
        } else {
          msg += ` | XML: wszystko OK`;
        }
        setUploadStatus({ type, status: 'success', message: msg });
      } else {
        // Import faktury PDF z walidacja XML
        const response = await api.post('/purchase-invoices/import-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = response.data;
        clearProgressInterval();
        setUploadProgress(100);

        if (data.success) {
          setUploadStatus({ type, status: 'success', message: data.message || 'Zaimportowano pomyslnie' });
          loadMenuStats();
          if (data.data?.invoice_id) {
            setTimeout(() => navigate(`/purchase-invoices/${data.data.invoice_id}`), 1500);
          }
        } else {
          setUploadStatus({ type, status: 'error', message: data.message });
        }
      }
    } catch (err) {
      clearProgressInterval();
      setUploadProgress(0);
      const errMsg = err?.message || 'Blad przesylania plikow';
      setUploadStatus({ type, status: 'error', message: errMsg });
    }
  };

  // ===================== GENERIC FILE UPLOAD =====================

  const handleFileUpload = async (type, file, opcje = {}) => {
    // Kreator czyta ten wynik, zeby wiedziec czy krok sie udal i czy backend
    // rozpoznal ZLY TYP PLIKU (cennik wrzucony w slot faktury i odwrotnie).
    const wynikPusty = {
      ok: false, errorCode: null, wykryto: null, oczekiwano: null,
      sugerowanyKrok: null, diagnostyka: null, moznaWymusic: false, komunikat: ''
    };
    if (!file) return { ...wynikPusty, komunikat: 'Nie wybrano pliku' };
    setUploadProgress(0);
    setUploadStatus({ type, status: 'uploading', message: 'Przesylanie pliku...' });

    const formData = new FormData();
    const fileKey = (type === 'xml' || type === 'paragon') ? 'xml_file' : (type === 'pdf' || type === 'pdf-products') ? 'pdf_file' : 'cennik_file';
    formData.append(fileKey, file);
    // Furtka dla uprawnionego: "to jednak jest cennik/faktura, przetworz mimo
    // rozpoznania". Backend sprawdza uprawnienie import_bez_cennika.
    if (opcje.wymusTyp) formData.append('wymus_typ', 'true');

    const progressSteps = (type === 'pdf' || type === 'pdf-products') ? [
      { percent: 10, msg: 'Przesylanie pliku...' },
      { percent: 25, msg: 'Konwersja PDF na obrazy...' },
      { percent: 50, msg: 'Skanowanie kodow kreskowych...' },
      { percent: 70, msg: 'Parsowanie danych faktury...' },
      { percent: 85, msg: 'Mapowanie produktow...' },
      { percent: 95, msg: 'Zapisywanie do bazy...' }
    ] : [
      { percent: 20, msg: 'Przesylanie pliku...' },
      { percent: 50, msg: 'Parsowanie danych...' },
      { percent: 80, msg: 'Zapisywanie...' }
    ];

    let stepIndex = 0;
    clearProgressInterval();
    progressIntervalRef.current = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setUploadProgress(progressSteps[stepIndex].percent);
        setUploadStatus({ type, status: 'uploading', message: progressSteps[stepIndex].msg });
        stepIndex++;
      }
    }, (type === 'pdf' || type === 'pdf-products') ? 3000 : 500);

    try {
      if (type === 'cennik') {
        const parseResponse = await api.post('/purchase-invoices/import-cennik', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: CENNIK_TIMEOUT
        });
        const parseData = parseResponse.data;
        clearProgressInterval();
        setUploadProgress(100);

        if (!parseData.success) {
          const b = odczytajBladBackendu(parseData, parseData.message || 'Blad parsowania cennika');
          setUploadStatus({ type, status: 'error', message: b.komunikat });
          return { ...wynikPusty, ...b, ok: false };
        }

        setCennikPreview(parseData.data);
        setShowCennikModal(true);
        const s = parseData.data.stats || {};
        const komunikat = `Znaleziono ${s.total || 0} produktow (${s.mapped || 0} zmapowanych, ${s.new || 0} nowych)`;
        setUploadStatus({ type, status: 'success', message: komunikat });
        return { ...wynikPusty, ok: true, komunikat };
      } else if (type === 'xml' || type === 'paragon') {
        // Paragon XML idzie przez TEN SAM podglad co faktura (krok 2): pokazuje
        // roznice cen/nazw i ostrzega o duplikacie ZANIM cokolwiek zapisze.
        // Parser (parse_invoice_xml) i endpointy preview-xml/save-xml sa wspolne.
        const parseResponse = await api.post('/purchase-invoices/preview-xml', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const parseData = parseResponse.data;
        clearProgressInterval();
        setUploadProgress(100);

        if (!parseData.success) {
          const b = odczytajBladBackendu(parseData, parseData.message || 'Blad parsowania faktury');
          setUploadStatus({ type, status: 'error', message: b.komunikat });
          return { ...wynikPusty, ...b, ok: false };
        }

        setFakturaPreview(parseData.data);
        setShowFakturaModal(true);
        const s = parseData.data.stats || {};
        const komunikat = `Faktura: ${parseData.data.invoice?.numer_faktury || 'brak numeru'} (${s.total_items || 0} pozycji)`;
        setUploadStatus({ type, status: 'success', message: komunikat });
        return { ...wynikPusty, ok: true, komunikat };
      } else if (type === 'pdf-products') {
        const response = await api.post('/purchase-invoices/preview-pdf-products', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = response.data;
        clearProgressInterval();
        setUploadProgress(100);

        if (!data.success) {
          const b = odczytajBladBackendu(data, data.message || 'Blad parsowania PDF');
          setUploadStatus({ type, status: 'error', message: b.komunikat });
          return { ...wynikPusty, ...b, ok: false };
        }

        setPdfProductsPreview(data.data);
        setShowPdfProductsModal(true);
        const s = data.data.stats || {};
        const komunikat = `Znaleziono ${s.total || 0} produktów (${s.mapped || 0} zmapowanych, ${s.new || 0} nowych)`;
        setUploadStatus({ type, status: 'success', message: komunikat });
        return { ...wynikPusty, ok: true, komunikat };
      } else {
        const endpoint = type === 'paragon' ? 'import-xml' : 'import-pdf';
        if (currentLocationId) formData.append('location_id', currentLocationId);
        if (type === 'pdf') formData.append('update_product_names', updateProductNames.toString());

        const response = await api.post(`/purchase-invoices/${endpoint}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const data = response.data;
        clearProgressInterval();
        setUploadProgress(100);
        if (data.success) {
          const komunikat = data.message || 'Zaimportowano pomyslnie';
          setUploadStatus({ type, status: 'success', message: komunikat });
          loadMenuStats();
          if (data.data?.invoice_id && autoNavigate) {
            setTimeout(() => navigate(`/purchase-invoices/${data.data.invoice_id}`), 1500);
          }
          return { ...wynikPusty, ok: true, komunikat };
        }
        const b = odczytajBladBackendu(data, data.message || 'Blad importu');
        setUploadStatus({ type, status: 'error', message: b.komunikat });
        return { ...wynikPusty, ...b, ok: false };
      }
    } catch (err) {
      clearProgressInterval();
      setUploadProgress(0);
      if (err?.isTimeout && type === 'cennik') {
        const komunikat = 'Cennik jest duzy, przetwarzanie moze potrwac do 2 minut. Odswiez liste produktow przed ponowna proba.';
        setUploadStatus({ type, status: 'error', message: komunikat });
        return { ...wynikPusty, ok: false, komunikat };
      }
      const b = odczytajBladBackendu(err, 'Blad przesylania pliku');
      const code = err?.status ? ` [HTTP ${err.status}]` : '';
      console.error('[Purchase import] error', err?.status, err);
      // Przy ZLY_TYP_PLIKU nie doklejamy kodu HTTP - komunikat idzie do banera kreatora
      const message = b.errorCode ? b.komunikat : `${b.komunikat}${code}`;
      setUploadStatus({ type, status: 'error', message });
      return { ...wynikPusty, ...b, ok: false, komunikat: message };
    }
  };

  // ===================== HELPERS =====================

  const clearUploadStatus = () => {
    setUploadStatus(null);
    setUploadProgress(0);
  };

  const closeOptimaModal = () => {
    setShowOptimaModal(false);
    setOptimaPreview(null);
  };

  const closeCennikModal = () => {
    setShowCennikModal(false);
    setCennikPreview(null);
    setCennikFilter('all');
    setCennikSearch('');
  };

  const closeFakturaModal = () => {
    setShowFakturaModal(false);
    setFakturaPreview(null);
  };

  // ===================== PDF PRODUCTS PREVIEW =====================

  const savePdfProductsImport = async () => {
    if (!pdfProductsPreview?.products) return;
    if (isSavingPdfProducts) return;
    setIsSavingPdfProducts(true);
    setUploadStatus({ type: 'pdf-products', status: 'uploading', message: 'Zapisywanie produktów...' });

    try {
      const response = await api.post('/purchase-invoices/save-pdf-products', {
        filename: pdfProductsPreview.filename,
        products: pdfProductsPreview.products,
        update_names: pdfProductsOptions.update_names,
        create_new: pdfProductsOptions.create_new
      });
      const data = response.data;
      if (data.success) {
        const s = data.data || {};
        setUploadStatus({
          type: 'pdf-products', status: 'success',
          message: `Zapisano: ${s.created || 0} nowych, ${s.updated || 0} zaktualizowanych, ${s.skipped || 0} pominiętych`
        });
        setShowPdfProductsModal(false);
        setPdfProductsPreview(null);
        loadMenuStats();
      } else {
        setUploadStatus({ type: 'pdf-products', status: 'error', message: data.message });
      }
    } catch (err) {
      setUploadStatus({ type: 'pdf-products', status: 'error', message: 'Błąd zapisywania produktów' });
    } finally {
      setIsSavingPdfProducts(false);
    }
  };

  const getFilteredPdfProducts = useCallback(() => {
    if (!pdfProductsPreview?.products) return [];
    let filtered = pdfProductsPreview.products;
    if (pdfProductsFilter === 'mapped') filtered = filtered.filter(p => p.is_mapped);
    else if (pdfProductsFilter === 'new') filtered = filtered.filter(p => !p.is_mapped);
    else if (pdfProductsFilter === 'price_changes') filtered = filtered.filter(p => p.is_mapped && p.price_differs);
    if (pdfProductsSearch.trim()) {
      const s = pdfProductsSearch.toLowerCase();
      filtered = filtered.filter(p =>
        (p.nazwa || '').toLowerCase().includes(s) ||
        (p.ean || '').toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [pdfProductsPreview, pdfProductsFilter, pdfProductsSearch]);

  const closePdfProductsModal = () => {
    setShowPdfProductsModal(false);
    setPdfProductsPreview(null);
    setPdfProductsFilter('all');
    setPdfProductsSearch('');
  };

  const closeMultiCennikModal = () => {
    setShowMultiCennikModal(false);
    setMultiCennikPreview(null);
    setMultiCennikFilter('all');
    setMultiCennikSearch('');
  };

  // ==========================================================================
  // CSV + XML Pricelist Import
  // ==========================================================================
  const [csvXmlCsvFile, setCsvXmlCsvFile] = useState(null);
  const [csvXmlXmlFile, setCsvXmlXmlFile] = useState(null);
  const [showCsvXmlModal, setShowCsvXmlModal] = useState(false);
  const [csvXmlPreview, setCsvXmlPreview] = useState(null);
  const [csvXmlOptions, setCsvXmlOptions] = useState({
    update_purchase_price: true,
    update_retail_price: true,
    update_vat: true,
    create_new: false,
    update_names: false,
  });
  const [csvXmlSearch, setCsvXmlSearch] = useState('');
  const [csvXmlFilter, setCsvXmlFilter] = useState('all');
  const [isSavingCsvXml, setIsSavingCsvXml] = useState(false);

  const handleCsvXmlImport = useCallback(async () => {
    if (!csvXmlCsvFile && !csvXmlXmlFile) {
      toast.error('Wybierz co najmniej jeden plik (CSV lub XML)');
      return;
    }
    try {
      setUploadStatus('Parsowanie plików CSV + XML...');
      const formData = new FormData();
      if (csvXmlCsvFile) formData.append('csv_file', csvXmlCsvFile);
      if (csvXmlXmlFile) formData.append('xml_file', csvXmlXmlFile);
      formData.append('apply', 'false');

      const res = await api.post('/purchase-invoices/import-csv-xml-pricelist', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadStatus(null);

      if (res.data?.success) {
        setCsvXmlPreview(res.data.data);
        setShowCsvXmlModal(true);
      } else {
        toast.error(res.data?.error || 'Błąd parsowania');
      }
    } catch (e) {
      setUploadStatus(null);
      toast.error(e?.message || 'Błąd importu CSV+XML');
    }
  }, [csvXmlCsvFile, csvXmlXmlFile]);

  const saveCsvXmlImport = useCallback(async () => {
    if (!csvXmlCsvFile && !csvXmlXmlFile) return;
    try {
      setIsSavingCsvXml(true);
      const formData = new FormData();
      if (csvXmlCsvFile) formData.append('csv_file', csvXmlCsvFile);
      if (csvXmlXmlFile) formData.append('xml_file', csvXmlXmlFile);
      formData.append('apply', 'true');
      formData.append('update_purchase_price', csvXmlOptions.update_purchase_price ? 'true' : 'false');
      formData.append('update_retail_price', csvXmlOptions.update_retail_price ? 'true' : 'false');
      formData.append('update_vat', csvXmlOptions.update_vat ? 'true' : 'false');
      formData.append('create_new', csvXmlOptions.create_new ? 'true' : 'false');
      formData.append('update_names', csvXmlOptions.update_names ? 'true' : 'false');

      const res = await api.post('/purchase-invoices/import-csv-xml-pricelist', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setIsSavingCsvXml(false);

      if (res.data?.success) {
        toast.success(res.data.message);
        setShowCsvXmlModal(false);
        setCsvXmlCsvFile(null);
        setCsvXmlXmlFile(null);
        setCsvXmlPreview(null);
        loadMenuStats();
      } else {
        toast.error(res.data?.error || 'Błąd zapisu');
      }
    } catch (e) {
      setIsSavingCsvXml(false);
      toast.error(e?.message || 'Błąd zapisu CSV+XML');
    }
  }, [csvXmlCsvFile, csvXmlXmlFile, csvXmlOptions, loadMenuStats]);

  const closeCsvXmlModal = () => {
    setShowCsvXmlModal(false);
    setCsvXmlPreview(null);
    setCsvXmlSearch('');
    setCsvXmlFilter('all');
  };

  return {
    // core data
    stats, loading, error, warehouses, loadMenuStats,

    // upload status
    uploadStatus, uploadProgress, clearUploadStatus,

    // file refs
    xmlInputRef, paragonInputRef, pdfInputRef, pdfProductsInputRef, cennikInputRef,
    cennikMultipleInputRef, optimaInputRef, sfdInputRef, xmlValidationInputRef,
    optimaUpdateCennikRef, optimaUpdateDokumentRef,
    optimaUpdateCennikFile, optimaUpdateDokumentFile,
    onOptimaUpdateCennik: setOptimaUpdateCennikFile,
    onOptimaUpdateDokument: setOptimaUpdateDokumentFile,
    onOptimaUpdateImport: handleOptimaUpdatePreview,
    optimaUpdatePreview, showOptimaUpdateModal, setShowOptimaUpdateModal,
    handleOptimaUpdateSave,

    // generic upload
    handleFileUpload,

    // PDF + XML validation flow
    pendingPdfFile, pendingPdfType, onPdfSelect, onXmlValidationSelect,

    // optima
    showOptimaModal, optimaPreview, optimaOptions, setOptimaOptions,
    isSaving, handleOptimaUpload, saveOptimaImport, closeOptimaModal,

    // SFD
    handleSfdUpload,

    // cennik single
    showCennikModal, cennikPreview, cennikOptions, setCennikOptions,
    cennikFilter, setCennikFilter, cennikSearch, setCennikSearch,
    isSavingCennik, saveCennikImport, getFilteredCennikProducts, closeCennikModal,

    // cennik multi
    multiCennikProgress, multiCennikPreview, showMultiCennikModal,
    multiCennikFilter, setMultiCennikFilter, multiCennikSearch, setMultiCennikSearch,
    isSavingMultiCennik, handleMultipleCennikUpload, saveMultiCennikImport,
    getFilteredMultiCennikProducts, closeMultiCennikModal,

    // paragony multi
    paragonMultipleInputRef, multiParagonProgress, multiParagonWynik,
    showMultiParagonModal, handleMultipleParagonUpload, closeMultiParagonModal,

    // faktura XML
    showFakturaModal, fakturaPreview, isSavingFaktura,
    saveFakturaImport, closeFakturaModal,
    // settery dla Kreatora (odtworzenie podgladu z sessionStorage po F5)
    setFakturaPreview, setShowFakturaModal, setShowCennikModal,

    // PDF option
    updateProductNames, setUpdateProductNames,

    // PDF products preview
    showPdfProductsModal, pdfProductsPreview, pdfProductsOptions, setPdfProductsOptions,
    pdfProductsFilter, setPdfProductsFilter, pdfProductsSearch, setPdfProductsSearch,
    isSavingPdfProducts, savePdfProductsImport, getFilteredPdfProducts, closePdfProductsModal,

    // Cennik only
    cennikOnlyFile, setCennikOnlyFile: (f) => setCennikOnlyFile(f),
    onCennikOnlyFileChange: (f) => setCennikOnlyFile(f),
    handleCennikOnlyImport,
    showCennikOnlyModal, setCennikOnlyModal: (v) => setShowCennikOnlyModal(v),
    cennikOnlyPreview, handleCennikOnlySave,

    // CSV + XML pricelist
    csvXmlCsvFile, setCsvXmlCsvFile: (f) => setCsvXmlCsvFile(f),
    csvXmlXmlFile, setCsvXmlXmlFile: (f) => setCsvXmlXmlFile(f),
    showCsvXmlModal, csvXmlPreview, csvXmlOptions, setCsvXmlOptions,
    csvXmlSearch, setCsvXmlSearch, csvXmlFilter, setCsvXmlFilter,
    isSavingCsvXml,
    handleCsvXmlImport, saveCsvXmlImport, closeCsvXmlModal,
  };
}
